import QRCode from 'qrcode';
import { useNotification } from '@kyvg/vue3-notification';
import { useCookies } from 'vue3-cookies';
import router from '@/router';
import store from '@/store';
import references from '@/configs/references.json';

const countryOptions = require('@/configs/countries');

const getReferencelabel = reference => {
  if (references[reference]) {
    return references[reference][store.getters.getLanguage];
  }
  return reference;
};

const getConfigurationContent = content => {
  if (content[store.getters.getLanguage]) {
    return content[store.getters.getLanguage];
  }

  if (content.display_content_language === store.getters.getLanguage) {
    return content.display_content_html;
  }

  return null;
};

const getCenterName = center_id => {
  const center = store.state.centers.find(c => c.uuid === center_id);
  return center ? center.center_name : 'NOT FOUND';
};

const generateQRcode = (value, options = { margin: 1, scale: 4 }) => {
  let qrCode = '';
  QRCode.toString(value, options, (err, svg) => qrCode = svg);
  return qrCode;
};

const isInImpersonateModeNotification = () => {
  const notification = useNotification();
  if (store.state.impersonateData) {
    notification.notify({
      type: 'error',
      title: getReferencelabel('IMPERSONATE_MODE_TRY_ACTION_NOTIFICATION')
    });
    return true;
  }

  return false;
};

const recursiveRouterExtraInfo = (routes, desiredRoute, previousRoute = null) => {
  let routeInfo = {};

  routes.map(route => {
    if (!isObjectEmpty(routeInfo)) {
      return route;
    }

    // We found desired submenus based on primary menu
    if (route.name === desiredRoute) {
      routeInfo = {
        name: route.name,
        parentName: previousRoute,
        component: route.component
      };

      return route;
    }

    // We recursive look for new routes in subchildrens, only if not home
    if (route.children) {
      const childrenSearch = recursiveRouterExtraInfo(route.children, desiredRoute, route.name);
      if (!isObjectEmpty(childrenSearch)) {
        routeInfo = childrenSearch;
      }
    }

    return route;
  });

  // Only return visible items
  return routeInfo;
};
const appendRouteItems = routes => {
  const foundChildrens = [];

  routes.map(child => {
    foundChildrens.push({
      visible: child.meta && child.meta.visible ? child.meta.visible : null,
      icon: child.meta && child.meta.icon ? child.meta.icon : '',
      reference: child.meta && child.meta.reference ? child.meta.reference : child.name,
      pathName: child.name,
      path: child.path
    });
  });

  return foundChildrens;
};

const recursiveRouterChildrenFinder = (routes, desiredRoute) => {
  // If we want home items, we append first items
  if (desiredRoute === 'Home') {
    return appendRouteItems(routes);
  }

  let foundChildrens = [];

  routes.map(route => {
    // We found desired submenus based on primary menu
    if (route.name === desiredRoute && route.children) {
      foundChildrens = appendRouteItems(route.children);
    }

    // We recursive look for new routes in subchildrens, only if not home
    if (route.children) {
      foundChildrens = [...foundChildrens, ...recursiveRouterChildrenFinder(route.children, desiredRoute)];
    }
  });

  return foundChildrens;
};

const findRouteInformation = routeName => recursiveRouterExtraInfo(router.options.routes, routeName);

const isObjectEmpty = obj => Object.keys(obj).length === 0;

const matchExact = (r, str) => {
  if (!str) {
    return false;
  }

  const match = String(str).match(r);
  return match && String(str) === match[0];
};

const genderVocabulary = gender => {
  if (store.getters.getLanguage === 'en') {
    return 'Born on';
  }

  let title = 'Né';

  switch (gender) {
    case 'mr':
      title = 'Né';
      break;
    case 'mme':
      title = 'Née';
      break;
    case 'miss':
      title = 'Née';
      break;
    default:
      break;
  }

  return `${title} le`;
};

const routeIsCurrentComponent = (routeName, component) => {
  const route = recursiveRouterExtraInfo(router.options.routes, routeName);

  if (route) {
    // eslint-disable-next-line no-underscore-dangle
    if (route.component.render === component.render) return true;
    return false;
  }

  return false;
};

const lengthBetween = (str, min, max) => (str ? str.length >= min && str.length <= max : false);

const menuHandler = routeName => {
  const menus = recursiveRouterChildrenFinder(router.options.routes, routeName);
  const navSection = store.getters.getSiteConfiguration('disabled_features');

  // Only return visible items and not disable path
  return menus.filter(v => v.visible && !navSection.includes(v.path));
};

const convertFieldHTMLContentToView = (field, largeText = false) => {
  if (!field) { return null; }

  // Replace newlines with br
  field = field.replace(/\n/g, '<br/>');

  if (largeText) {
    field = field.replace(/<ul>/g, '<ul class="list-reset lg-list pl1 inline-block">');
  } else {
    field = field.replace(/<ul>/g, '<ul class="list-reset pl1 inline-block">');
  }

  // Add identation with paddings
  // ql injected on pim
  const elementsWithIdentation = field.match(/<p class='ql-indent-.?'>/g);

  if (elementsWithIdentation) {
    elementsWithIdentation.forEach(element => {
      field = field.replace(new RegExp(element), `<p class="pl${element[element.length - 3] > 4 ? 4 : element[element.length - 3]}">`);
    });
  }

  return field.replace(/<li>/g, '<li class="list-style-item">') // all li of first level
    .replace(/<li class='ql-indent-.?'>/g, '<li class="list-style-item ml3">') // all li's of seconds levels
    .replace(/<h1>/g, '<h2 class="h1 caps mb0">').replace(/<\/h1>/g, '</h2>')
    .replace(/target='_blank'>/g, "target='_blank'><u>")
    .replace(/<\/a>/g, '</u></a>')
    .replace(/<h5>/g, '<span class="h7n">')
    .replace(/<\/h5>/g, '</span>')
    .replace(/<p>/g, '<p style="margin:0">');
};

const goToHello = (roomId = null) => {
  const { cookies } = useCookies();
  const room = roomId ? `/${roomId}` : '';
  window.open(`${process.env.VUE_APP_HELLO_URL}${room}?token=${cookies.get('oauth2_cookie')}`);
};

export {
  findRouteInformation,
  isObjectEmpty,
  matchExact,
  genderVocabulary,
  lengthBetween,
  menuHandler,
  generateQRcode,
  isInImpersonateModeNotification,
  getCenterName,
  getReferencelabel,
  getConfigurationContent,
  countryOptions,
  routeIsCurrentComponent,
  convertFieldHTMLContentToView,
  goToHello
};
